import { useCallback, useState } from 'react'
import { useAppDispatch } from '@user/store/hooks'
import {
  AssignmentRuleDTO,
  removeAssignmentRule,
  toggleAssignmentRulePause,
} from '@user/store/leadAssignmentRule'
import { toast } from '@sales-center/components'
import { parseCoverageError } from '../../utils/coverageError'

export function useHandlers(assignment: AssignmentRuleDTO) {
  const { id, enabled } = assignment
  const dispatch = useAppDispatch()
  const [deleteOpened, setDeleteOpened] = useState<boolean>(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [coverageBlockLabels, setCoverageBlockLabels] = useState<
    string[] | null
  >(null)

  const handleDeleteClick = useCallback(
    async (id: number) => {
      setDeleteLoading(true)
      const result = await dispatch(removeAssignmentRule(id))

      if (removeAssignmentRule.fulfilled.match(result)) {
        setDeleteLoading(false)
        toast.success('Assignment rule deleted.')
      } else {
        setDeleteLoading(false)
        const labels = parseCoverageError(result.payload)
        if (labels) {
          setCoverageBlockLabels(labels)
        } else {
          toast.error('Unexpected error occurred.')
        }
      }

      setDeleteOpened(false)
    },
    [dispatch],
  )

  const closeDeleteDialog = useCallback(() => {
    setDeleteOpened(false)
  }, [])

  const openDeleteDialog = useCallback(() => {
    setDeleteOpened(true)
  }, [])

  const togglePause = useCallback(async () => {
    const result = await dispatch(
      toggleAssignmentRulePause({ id, toggleState: !enabled }),
    )

    if (toggleAssignmentRulePause.fulfilled.match(result)) {
      toast.success('Assignment rule updated.')
    } else {
      const labels = parseCoverageError(result.payload)
      if (labels) {
        setCoverageBlockLabels(labels)
      } else {
        toast.error('Unexpected error occurred.')
      }
    }
  }, [dispatch, enabled, id])

  const closeCoverageBlock = useCallback(() => {
    setCoverageBlockLabels(null)
  }, [])

  return {
    handleDeleteClick,
    deleteOpened,
    closeDeleteDialog,
    openDeleteDialog,
    togglePause,
    deleteLoading,
    coverageBlockLabels,
    closeCoverageBlock,
  }
}
